import { useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { ThumbsUp, ThumbsDown, Flag, CheckCircle, Pencil } from "lucide-react";
import { StarRating } from "./StarRating";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface ReviewCardProps {
  review: any;
  currentUserId?: string;
  onVote?: () => void;
  onReport?: () => void;
  onEdit?: (review: any) => void;
}

export const ReviewCard = ({ review, currentUserId, onVote, onReport, onEdit }: ReviewCardProps) => {
  const { t } = useTranslation();
  const [voting, setVoting] = useState(false);

  const handleVote = async (voteType: "helpful" | "not_helpful") => {
    setVoting(true);
    try {
      const { data: existingVote } = await supabase
        .from("review_votes")
        .select()
        .eq("review_id", review.id)
        .eq("user_id", (await supabase.auth.getUser()).data.user?.id)
        .maybeSingle();

      if (existingVote) {
        if (existingVote.vote_type === voteType) {
          await supabase.from("review_votes").delete().eq("id", existingVote.id);
          toast.success(t("reviews.voteRemoved"));
        } else {
          await supabase
            .from("review_votes")
            .update({ vote_type: voteType })
            .eq("id", existingVote.id);
          toast.success(t("reviews.voteUpdated"));
        }
      } else {
        const user = await supabase.auth.getUser();
        await supabase.from("review_votes").insert({
          review_id: review.id,
          user_id: user.data.user?.id,
          vote_type: voteType,
        });
        toast.success(t("reviews.voteAdded"));
      }
      
      onVote?.();
    } catch (error) {
      console.error("Error voting:", error);
      toast.error(t("reviews.voteError"));
    } finally {
      setVoting(false);
    }
  };

  const handleReport = () => {
    onReport?.();
  };

  const handleEdit = () => {
    onEdit?.(review);
  };

  // Build reviewer name from joined profile data
  const getReviewerName = () => {
    const reviewer = review.reviewer;
    if (reviewer?.first_name) {
      const lastName = reviewer.last_name ? `${reviewer.last_name.charAt(0)}.` : '';
      return `${reviewer.first_name} ${lastName}`.trim();
    }
    return t("reviews.anonymous", "Utente");
  };

  const isOwnReview = currentUserId && review.user_id === currentUserId;

  return (
    <div className="bg-white border-[3px] border-dark shadow-brutal p-6">
      {/* Header */}
      <div className="flex justify-between mb-4">
        <div className="flex items-center gap-3">
          {review.reviewer?.profile_image ? (
            <img 
              src={review.reviewer.profile_image} 
              alt={getReviewerName()}
              className="w-10 h-10 rounded-full border-[2px] border-dark object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-primary border-[2px] border-dark flex items-center justify-center font-bold text-white">
              {review.reviewer?.first_name?.[0]?.toUpperCase() || "U"}
            </div>
          )}
          <div>
            <div className="font-bold">{getReviewerName()}</div>
            <div className="text-sm text-muted-foreground">
              {format(new Date(review.created_at), "dd MMM yyyy")}
              {review.is_verified_purchase && (
                <span className="ml-2 text-success inline-flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  {t("reviews.verified")}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isOwnReview && onEdit && (
            <button
              onClick={handleEdit}
              className="text-sm text-primary hover:text-primary/80 flex items-center gap-1 transition-colors"
              title={t("reviews.editReview", "Modifica recensione")}
            >
              <Pencil className="w-4 h-4" />
            </button>
          )}
          <StarRating rating={review.rating} readonly />
        </div>
      </div>

      {/* Title and Comment */}
      <h3 className="font-heading text-lg font-bold mb-2">{review.title}</h3>
      <p className="text-foreground mb-4">{review.comment}</p>

      {/* Pros and Cons */}
      {(review.pros || review.cons) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {review.pros && (
            <div className="bg-green-50 border-[2px] border-success p-3">
              <div className="font-bold text-success mb-1">
                {t("reviews.pros")}
              </div>
              <p className="text-sm">{review.pros}</p>
            </div>
          )}
          {review.cons && (
            <div className="bg-red-50 border-[2px] border-destructive p-3">
              <div className="font-bold text-destructive mb-1">
                {t("reviews.cons")}
              </div>
              <p className="text-sm">{review.cons}</p>
            </div>
          )}
        </div>
      )}

      {/* Media */}
      {review.media && review.media.length > 0 && (
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {review.media.map((media: any) => (
            <img
              key={media.id}
              src={media.media_url}
              alt={media.caption || "Review image"}
              className="w-20 h-20 object-cover border-[2px] border-dark cursor-pointer hover:scale-105 transition-transform"
            />
          ))}
        </div>
      )}

      {/* Professional Response */}
      {review.response_text && (
        <div className="bg-surface border-l-[4px] border-primary p-4 mt-4">
          <div className="font-bold mb-2">{t("reviews.professionalResponse")}</div>
          <p className="text-sm">{review.response_text}</p>
          <div className="text-xs text-muted-foreground mt-2">
            {format(new Date(review.response_date), "dd MMM yyyy")}
          </div>
        </div>
      )}

      {/* Footer Actions */}
      <div className="flex justify-between items-center mt-4 pt-4 border-t-[2px] border-dark">
        <div className="flex gap-4">
          <button
            onClick={() => handleVote("helpful")}
            disabled={voting}
            className="text-sm hover:text-primary flex items-center gap-1 transition-colors"
          >
            <ThumbsUp className="w-4 h-4" />
            {t("reviews.helpful")} ({review.helpful_count || 0})
          </button>
          <button
            onClick={() => handleVote("not_helpful")}
            disabled={voting}
            className="text-sm hover:text-primary flex items-center gap-1 transition-colors"
          >
            <ThumbsDown className="w-4 h-4" />
            {t("reviews.notHelpful")}
          </button>
        </div>

        <button
          onClick={handleReport}
          className="text-sm text-muted-foreground hover:text-destructive flex items-center gap-1 transition-colors"
        >
          <Flag className="w-4 h-4" />
          {t("reviews.report")}
        </button>
      </div>
    </div>
  );
};
